import React, { Component } from 'react';
import { BadgeCheck, Sparkles, Globe, ShieldCheck } from 'lucide-react';
import { bool, object, array } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';

import { useConfiguration } from '../../context/configurationContext';
import { LayoutSingleColumn, Page } from '../../components/index.js';
import TopbarContainer from '../TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import { propTypes } from '../../util/types';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  getFeaturedArtCategoryArtists,
  isFeaturedArtCategoryArtistsFetchInProgress,
  getFeaturedArtCategoryArtistsError,
  getJudaicaCategoryArtists,
  isJudaicaCategoryArtistsFetchInProgress,
  getJudaicaCategoryArtistsError
} from '../../ducks/artistUsernames.duck';
import {
  loadHeroContent,
  getHeroContent,
  isHeroContentLoading
} from '../../ducks/sanityContent.duck';
import { validProps } from '../PageBuilder/Field';
import Hero from './Hero';
import ArtSection from './ArtSection';
import JudaicaSection from './JudaicaSection';
import FeaturesSection from './FeaturesSection';

// Trust Banner Component
const TrustBanner = () => {
  const trustItems = [
    { icon: BadgeCheck, text: 'Verified Jewish Artists' },
    { icon: Sparkles, text: 'Handcrafted Quality' },
    { icon: Globe, text: 'Worldwide Delivery' },
    { icon: ShieldCheck, text: 'Purchase Protection' },
  ];

  return (
    <div className="w-full py-6 max-w-7xl mx-auto">
      <div className=" rounded-2xl py-5 md:py-6 px-6">
        <div className="flex flex-wrap justify-between items-start gap-x-8 gap-y-4 md:gap-x-12">
          {trustItems.map(({ icon: Icon, text }) => (
            <div key={text} className="flex flex-col items-center gap-2 p-4">
              <div className="bg-gray-100 rounded-full p-3">
                <Icon
                  className="size-5 md:size-6"
                  style={{ color: 'var(--marketplaceColor)', fill: 'none' }}
                />
              </div>
              <span className="font-semibold text-gray-800 text-sm md:text-base text-center">
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Announcement Banner Component
const AnnouncementBanner = () => {
  return (
    <div className="bg-black w-full">
      <div className="flex items-center justify-center py-0 px-4 md:px-4">
        <div className="max-w-6xl w-full text-center">
          <p className="text-white text-sm md:text-lg lg:text-xl font-normal leading-relaxed m-0">
            Introducing Joodaic: A first-of-its-kind marketplace for Jewish makers and buyers.
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper function to extract section by name
const extractSectionByName = (pageAssetsData, sectionName) => {
  if (!pageAssetsData?.landingPage?.data?.sections) return null;

  return pageAssetsData.landingPage.data.sections.find(
    section => section.sectionName?.toLowerCase() === sectionName.toLowerCase()
  );
};

class LandingPageComponent extends Component {
  componentDidMount() {
    const { onLoadHeroContent, onLoadCollections } = this.props;
    // Fetch hero content from Sanity when component mounts
    if (onLoadHeroContent) {
      onLoadHeroContent().catch(e => {
        // Error is already handled in Redux, just log for debugging
        console.error('Failed to load hero content from Sanity:', e);
      });
    }
  }

  render() {
    const {
      pageAssetsData,
      inProgress,
      error,
      currentPage,
      featuredListings,
      featuredListingsInProgress,
      featuredListingsError,
      featuredJudaicaListings,
      featuredJudaicaListingsInProgress,
      featuredJudaicaListingsError,
      artCategoryArtists,
      artCategoryArtistsInProgress,
      artCategoryArtistsError,
      judaicaCategoryArtists,
      judaicaCategoryArtistsInProgress,
      judaicaCategoryArtistsError,
      currentUser,
      scrollingDisabled,
      heroContent,
      heroContentLoading,
      intl,
      ...rest
    } = this.props;

    // Extract metadata from pageAssetsData if available
    const { pageTitle, pageDescription, socialSharing } = pageAssetsData?.landingPage?.data?.meta || {};

    // Use validProps to extract content if the data exists (with empty fieldComponents)
    const title = validProps(pageTitle, {})?.content;
    const description = validProps(pageDescription, {})?.content;
    const socialSharingData = validProps(socialSharing, {});

    // Create schema for SEO (similar to PageBuilder)
    const schemaImage = socialSharingData?.images1200?.[0]?.url;
    const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
    const schema = {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      description: description,
      name: title,
      ...schemaImageMaybe,
    };

    // Show error state
    if (error) {
      return (
        <Page
          scrollingDisabled={scrollingDisabled}
          description={description}
          title={title}
          schema={schema}
          socialSharing={socialSharingData}
        >
          <div className="flex items-center justify-center min-h-[50vh] px-6 py-15">
            <div className="text-center max-w-lg">
              <h2 className="text-2xl font-bold text-red-600 mb-4">Unable to load page content</h2>
              <p className="text-gray-700">Please try refreshing the page or contact support if the problem persists.</p>
            </div>
          </div>
        </Page>
      );
    }

    // Extract specific sections from CMS data
    // Remove featuredSellersSection and featuredJudaicaSellersSection - components now get data directly from Redux
    const collectionsSection = extractSectionByName(pageAssetsData, 'collections');
    const judaicaCollectionsSection = extractSectionByName(pageAssetsData, 'judaica collections');

    // Check if we have any content to show
    const hasContent = collectionsSection || judaicaCollectionsSection;

    // Only show loading if we have no content AND still loading (following PageBuilder pattern)
    if (!hasContent && inProgress) {
      return (
        <Page
          scrollingDisabled={scrollingDisabled}
          description={description}
          title={title}
          schema={schema}
          socialSharing={socialSharingData}
        >
          <div className="flex items-center justify-center min-h-[50vh] px-6 py-15">
            <div className="text-xl text-gray-600 text-center">Loading...</div>
          </div>
        </Page>
      );
    }

    // If no sections found after loading, show a message
    if (!hasContent && !inProgress) {
      return (
        <Page
          scrollingDisabled={scrollingDisabled}
          description={description}
          title={title}
          schema={schema}
          socialSharing={socialSharingData}
        >
          <div className="flex items-center justify-center min-h-[50vh] px-6 py-15">
            <div className="text-center max-w-lg">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">No content available</h2>
              <p className="text-gray-600">The requested sections are not available in the CMS.</p>
            </div>
          </div>
        </Page>
      );
    }

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
        socialSharing={socialSharingData}
      >
        <LayoutSingleColumn
          topbar={<TopbarContainer currentPage={currentPage} />}
          footer={<FooterContainer showStayUpdated={true} />}
        >
          <div className="w-full bg-[#F7F7F5]">
            <AnnouncementBanner />
            <Hero
              heroContent={heroContent}
              artists={[...(artCategoryArtists || []), ...(judaicaCategoryArtists || [])]}
            />

            <TrustBanner />

            <JudaicaSection
              judaicaCollectionsSection={judaicaCollectionsSection}
              featuredJudaicaListings={featuredJudaicaListings}
              featuredJudaicaListingsInProgress={featuredJudaicaListingsInProgress}
              featuredJudaicaListingsError={featuredJudaicaListingsError}
              judaicaCategoryArtists={judaicaCategoryArtists}
              judaicaCategoryArtistsInProgress={judaicaCategoryArtistsInProgress}
              judaicaCategoryArtistsError={judaicaCategoryArtistsError}
              currentUser={currentUser}
            />

            <ArtSection
              collectionsSection={collectionsSection}
              featuredListings={featuredListings}
              featuredListingsInProgress={featuredListingsInProgress}
              featuredListingsError={featuredListingsError}
              artCategoryArtists={artCategoryArtists}
              artCategoryArtistsInProgress={artCategoryArtistsInProgress}
              artCategoryArtistsError={artCategoryArtistsError}
              currentUser={currentUser}
            />

            <FeaturesSection />
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  currentPage: object,
  featuredListings: array,
  featuredListingsInProgress: bool,
  featuredListingsError: object,
  featuredJudaicaListings: array,
  featuredJudaicaListingsInProgress: bool,
  featuredJudaicaListingsError: object,
  artCategoryArtists: array,
  artCategoryArtistsInProgress: bool,
  artCategoryArtistsError: object,
  judaicaCategoryArtists: array,
  judaicaCategoryArtistsInProgress: bool,
  judaicaCategoryArtistsError: object,
  currentUser: object,
  scrollingDisabled: bool,
  heroContent: object,
  heroContentLoading: bool,
  onLoadHeroContent: object,
  intl: object,
};

// Redux state mapping
const mapStateToProps = state => {
  const {
    featuredListingIds,
    featuredListingsInProgress,
    featuredListingsError,
    featuredJudaicaListingIds,
    featuredJudaicaListingsInProgress,
    featuredJudaicaListingsError,
  } = state.LandingPage || {};

  const {
    pageAssetsData,
    inProgress: pageAssetsInProgress,
    error: pageAssetsError,
  } = state.hostedAssets || {};

  // Convert listing IDs to entity references for denormalization
  const featuredListingRefs = featuredListingIds
    ? featuredListingIds.map(id => ({ id, type: 'listing' }))
    : [];
  const featuredJudaicaListingRefs = featuredJudaicaListingIds
    ? featuredJudaicaListingIds.map(id => ({ id, type: 'listing' }))
    : [];

  return {
    scrollingDisabled: isScrollingDisabled(state),
    featuredListings: getMarketplaceEntities(state, featuredListingRefs),
    featuredListingsInProgress,
    featuredListingsError,
    featuredJudaicaListings: getMarketplaceEntities(state, featuredJudaicaListingRefs),
    featuredJudaicaListingsInProgress,
    featuredJudaicaListingsError,
    artCategoryArtists: getFeaturedArtCategoryArtists(state),
    artCategoryArtistsInProgress: isFeaturedArtCategoryArtistsFetchInProgress(state),
    artCategoryArtistsError: getFeaturedArtCategoryArtistsError(state),
    judaicaCategoryArtists: getJudaicaCategoryArtists(state),
    judaicaCategoryArtistsInProgress: isJudaicaCategoryArtistsFetchInProgress(state),
    judaicaCategoryArtistsError: getJudaicaCategoryArtistsError(state),
    pageAssetsData,
    inProgress: pageAssetsInProgress,
    error: pageAssetsError,
    currentUser: state.user.currentUser,
    heroContent: getHeroContent(state),
    heroContentLoading: isHeroContentLoading(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onLoadHeroContent: () => dispatch(loadHeroContent()),
});

// Functional wrapper component to get config using useConfiguration hook
const EnhancedLandingPage = props => {
  const config = useConfiguration();

  return <LandingPageComponent config={config} {...props} />;
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle method.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(EnhancedLandingPage);

export default LandingPage;
